import React, { useState, useEffect, Suspense, lazy } from 'react';
import { RouterProvider, useRouter } from './context/RouterContext';
import { AuthProvider } from './context/AuthContext';
import { ContentProvider } from './context/ContentContext';
import { ThemeProvider } from './context/ThemeContext';
import { SearchModal } from './components/SearchModal';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { MobileBottomNav } from './components/MobileBottomNav';
import { AiSupportWidget } from './components/AiSupportWidget';
import { FloatingWhatsAppCTA } from './components/FloatingWhatsAppCTA';
import { HomePage } from './pages/HomePage';

// Dedicated Subpages - Code-split via React.lazy for ultra-lightweight initial home payload
const ProductPage = lazy(() => import('./pages/ProductPage').then(m => ({ default: m.ProductPage })));
const FeaturesPage = lazy(() => import('./pages/FeaturesPage').then(m => ({ default: m.FeaturesPage })));
const ProxyCenterPage = lazy(() => import('./pages/ProxyCenterPage').then(m => ({ default: m.ProxyCenterPage })));
const ToolsPage = lazy(() => import('./pages/ToolsPage').then(m => ({ default: m.ToolsPage })));
const TutorialsPage = lazy(() => import('./pages/TutorialsPage').then(m => ({ default: m.TutorialsPage })));
const JournalPage = lazy(() => import('./pages/JournalPage').then(m => ({ default: m.JournalPage })));
const UpdatesPage = lazy(() => import('./pages/UpdatesPage').then(m => ({ default: m.UpdatesPage })));
const ResellerPage = lazy(() => import('./pages/ResellerPage').then(m => ({ default: m.ResellerPage })));
const ComparePage = lazy(() => import('./pages/ComparePage').then(m => ({ default: m.ComparePage })));
const PricingPage = lazy(() => import('./pages/PricingPage').then(m => ({ default: m.PricingPage })));
const DownloadPage = lazy(() => import('./pages/DownloadPage').then(m => ({ default: m.DownloadPage })));
const ContactPage = lazy(() => import('./pages/ContactPage').then(m => ({ default: m.ContactPage })));
const AdminPage = lazy(() => import('./pages/AdminPage').then(m => ({ default: m.AdminPage })));
const ChangelogPage = lazy(() => import('./pages/ChangelogPage').then(m => ({ default: m.ChangelogPage })));
const BlogPage = lazy(() => import('./pages/BlogPage').then(m => ({ default: m.BlogPage })));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage').then(m => ({ default: m.BlogPostPage })));
const AboutPage = lazy(() => import('./pages/AboutPage').then(m => ({ default: m.AboutPage })));
const FaqPage = lazy(() => import('./pages/FaqPage').then(m => ({ default: m.FaqPage })));
const SupportPage = lazy(() => import('./pages/SupportPage').then(m => ({ default: m.SupportPage })));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage').then(m => ({ default: m.PrivacyPolicyPage })));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage').then(m => ({ default: m.TermsOfServicePage })));
const ToolsDirectoryPage = lazy(() => import('./pages/ToolsDirectoryPage').then(m => ({ default: m.ToolsDirectoryPage })));
const ToolDetailPage = lazy(() => import('./pages/ToolDetailPage').then(m => ({ default: m.ToolDetailPage })));
const ComparisonsPage = lazy(() => import('./pages/ComparisonsPage').then(m => ({ default: m.ComparisonsPage })));
const TopicalSeoPage = lazy(() => import('./pages/TopicalSeoPage').then(m => ({ default: m.TopicalSeoPage })));

const TOPICAL_SLUGS = [
  'facebook-marketplace-automation',
  'facebook-marketplace-automation-software',
  'facebook-marketplace-tools',
  'facebook-marketplace-seller-tools',
  'facebook-marketplace-listing-software',
  'facebook-marketplace-listing-tool',
  'facebook-marketplace-auto-poster',
  'facebook-marketplace-bulk-posting',
  'facebook-marketplace-auto-reply',
  'facebook-marketplace-ai-tools',
  'facebook-marketplace-ai-listing-tool',
  'facebook-marketplace-image-generator',
  'facebook-marketplace-relisting-tool',
  'facebook-marketplace-extensions',
  'free-facebook-marketplace-tools'
];

const AppContent: React.FC = () => {
  const { currentPath } = useRouter();
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsSearchOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Render dedicated subpage or main landing page
  const renderCurrentView = () => {
    // Normalize path by stripping any trailing slashes (/tools/ -> /tools)
    const cleanPath = (currentPath || '/').replace(/\/+$/, '') || '/';

    // 1. Dynamic Blog Article Routes (/blog/:slug)
    if (cleanPath.startsWith('/blog/')) {
      const slug = cleanPath.replace('/blog/', '').replace(/\/+$/, '');
      if (slug) {
        return <BlogPostPage slug={slug} />;
      }
      return <BlogPage />;
    }

    // 2. Topical SEO Landing Pages (e.g. /facebook-marketplace-automation)
    const cleanPathSlug = cleanPath.replace(/^\//, '').replace(/\/+$/, '');
    if (TOPICAL_SLUGS.includes(cleanPathSlug)) {
      return <TopicalSeoPage slug={cleanPathSlug} />;
    }

    // 3. Tool Directory & Tool Detail Routes
    if (cleanPath === '/tools' || cleanPath === '/tools/facebook-marketplace-tools') {
      return <ToolsDirectoryPage />;
    }
    if (cleanPath.startsWith('/tools/')) {
      const toolSlug = cleanPath.replace('/tools/', '').replace(/\/+$/, '');
      if (!toolSlug) {
        return <ToolsDirectoryPage />;
      }
      return <ToolDetailPage slug={toolSlug} />;
    }

    // 4. Software Comparisons Routes
    if (cleanPath === '/comparisons' || cleanPath === '/compare') {
      return <ComparisonsPage />;
    }
    if (cleanPath.startsWith('/comparisons/')) {
      const compSlug = cleanPath.replace('/comparisons/', '').replace(/\/+$/, '');
      if (!compSlug) {
        return <ComparisonsPage />;
      }
      return <ComparisonsPage initialSlug={compSlug} />;
    }

    // 5. Download Hub Routes
    if (
      cleanPath === '/download' || 
      cleanPath === '/downloads' || 
      cleanPath === '/downloads/lashary-fb-market-suite'
    ) {
      return <DownloadPage />;
    }

    // 6. Changelog / Release Details
    if (cleanPath === '/changelog' || cleanPath === '/updates' || cleanPath.startsWith('/changelog/')) {
      return <ChangelogPage />;
    }

    // 7. Exact Static Routes
    switch (cleanPath) {
      case '/features':
        return <FeaturesPage />;
      case '/features/proxy':
        return <ProxyCenterPage />;
      case '/blog':
        return <BlogPage />;
      case '/about':
        return <AboutPage />;
      case '/faq':
        return <FaqPage />;
      case '/support':
        return <SupportPage />;
      case '/privacy':
        return <PrivacyPolicyPage />;
      case '/terms':
        return <TermsOfServicePage />;
      case '/pricing':
        return <PricingPage />;
      case '/tutorials':
        return <TutorialsPage />;
      case '/product':
        return <ProductPage />;
      case '/reseller':
        return <ResellerPage />;
      case '/contact':
        return <ContactPage />;
      case '/journal':
        return <JournalPage />;
      case '/admin':
        return <AdminPage />;
      default:
        if (cleanPath.startsWith('/admin')) {
          return <AdminPage />;
        }
        // Default Polished Commercial Software Home View
        return <HomePage />;
    }
  };

  return (
    <div className="min-h-screen bg-canvas text-slate-900 dark:text-slate-100 flex flex-col selection:bg-rose/20 selection:text-rose relative pb-16 sm:pb-0 transition-colors">
      {/* Global Command Palette / Search Modal */}
      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />

      {/* Floating Glass Navigation */}
      <Navbar onOpenSearch={() => setIsSearchOpen(true)} />

      {/* Main Content Assembly with Page Transitions */}
      <main className="flex-1 pt-16">
        <div key={currentPath} className="animate-page-enter">
          <Suspense fallback={null}>
            {renderCurrentView()}
          </Suspense>
        </div>
      </main>

      {/* Global Customer-Facing AI Support Copilot */}
      <AiSupportWidget />

      {/* Global Founder WhatsApp Direct CTA */}
      <FloatingWhatsAppCTA />

      {/* Mobile Native App Bottom Navigation Bar */}
      <MobileBottomNav />

      {/* Global Brand & Legal Footer */}
      <Footer />
    </div>
  );
};

export const App: React.FC = () => {
  return (
    <ThemeProvider>
      <ContentProvider>
        <AuthProvider>
          <RouterProvider>
            <AppContent />
          </RouterProvider>
        </AuthProvider>
      </ContentProvider>
    </ThemeProvider>
  );
};

export default App;
